import React, { useEffect, useState } from "react";
import { CalendarClock, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { endpoints } from "@/lib/api";

const typeTone = {
  Meeting: "bg-blue-50 text-blue-700 border-blue-100",
  Audit: "bg-purple-50 text-purple-700 border-purple-100",
  Call: "bg-sky-50 text-sky-700 border-sky-100",
  Review: "bg-indigo-50 text-indigo-700 border-indigo-100",
};

const fmtDay = (d) => new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

export default function UpcomingAppointments({ refreshKey }) {
  const [items, setItems] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    endpoints.listAppointments().then((all) => {
      const list = all.filter((a) => a.date > today).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
      setItems(list);
    }).catch(() => {});
  }, [refreshKey, today]);

  return (
    <div data-testid="upcoming-appointments-card" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 flex items-center justify-center">
          <CalendarClock className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-[15px] tracking-tight">Upcoming Appointments</h2>
          <p className="text-xs text-slate-500">Next in your calendar</p>
        </div>
        <Badge variant="secondary" className="rounded-full">{items.length}</Badge>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
            <CalendarClock className="h-5 w-5 text-slate-400" />
          </div>
          No upcoming appointments.
        </div>
      ) : (
        <ScrollArea className="h-[300px] pr-2">
          <div className="space-y-3">
            {items.map((a) => (
              <div key={a.id} data-testid={`upcoming-appt-${a.id}`} className="rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3 hover:border-indigo-300/70 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{fmtDay(a.date)} · {a.time}</div>
                  <Badge variant="outline" className={`text-[10px] rounded-full ${typeTone[a.type] || ""}`}>{a.type}</Badge>
                </div>
                <div className="text-sm font-semibold mt-1 truncate">{a.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                  <Users className="h-3 w-3" />
                  <span className="truncate">{a.client || "Internal"}</span>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
